import React, { createContext, useContext, useMemo, useState } from "react";
import Measure, { ContentRect } from "react-measure";

// Breakpoints based on the actual width of the main layout area, not viewport.
// Mirrors $xs/$s/$m/$ml/$l/$xl/$xxl-screen-width values from _variables.scss.
const BREAKPOINTS = {
  xxs: 320,
  xs: 375,
  s: 450,
  m: 768,
  ml: 900,
  l: 1024,
  xl: 1440,
  xxl: 2560,
} as const;

type MainWidthContextValue = {
  width: number;
  classes: string;
};

const MainWidthContext = createContext<MainWidthContextValue>({
  width: 0,
  classes: "",
});

export const useMainWidth = () => useContext(MainWidthContext);

const getMainWidthClasses = (width: number): string => {
  return Object.entries(BREAKPOINTS)
    .filter(([, min]) => width >= min)
    .map(([key]) => `${key}-main-container`)
    .join(" ");
};

export const MainWidthProvider = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const [width, setWidth] = useState(0);

  const onResize = (rect: ContentRect) => {
    if (rect.bounds?.width != null) {
      setWidth(rect.bounds.width);
    }
  };

  const value = useMemo<MainWidthContextValue>(
    () => ({ width, classes: getMainWidthClasses(width) }),
    [width],
  );

  return (
    <MainWidthContext.Provider value={value}>
      <Measure bounds onResize={onResize}>
        {({ measureRef }) => (
          <div ref={measureRef} className={className}>
            {children}
          </div>
        )}
      </Measure>
    </MainWidthContext.Provider>
  );
};
